import React, { useState, useEffect } from 'react';
import { auth, db, storage } from '../firebase/firebaseConfig';
import { doc, getDoc, updateDoc, setDoc } from 'firebase/firestore';
import { updatePassword, updateProfile, EmailAuthProvider, reauthenticateWithCredential, linkWithCredential } from 'firebase/auth';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useNavigate } from 'react-router-dom';
import { customToast } from '../utils/toastUtils';
import EmailCollectionModal from '../components/EmailCollectionModal';
import './ProfilePage.css';

const ProfilePage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [refreshingStats, setRefreshingStats] = useState(false);

  // Form states
  const [displayName, setDisplayName] = useState('');
  const [mobileNumber, setMobileNumber] = useState('');
  const [tempMobileNumber, setTempMobileNumber] = useState('');
  const [photoURL, setPhotoURL] = useState('');
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  // Password change states
  const [showPassword, setShowPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Email update modal for phone-authenticated users
  const [showEmailModal, setShowEmailModal] = useState(false);

  useEffect(() => {
    // Check authentication state
    const checkAuth = () => {
      const user = auth.currentUser;
      
      if (!user) {
        // Wait a bit for auth state to initialize
        setTimeout(() => {
          const retryUser = auth.currentUser;
          if (!retryUser) {
            customToast.error('Please login to view profile');
            navigate('/');
          } else {
            fetchUserData();
          }
        }, 1000);
      } else {
        fetchUserData();
      }
    };
    
    checkAuth();

    // Refresh data when page becomes visible (user returns from another tab/page)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && auth.currentUser) {
        fetchUserData();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const fetchUserData = async () => {
    try {
      const user = auth.currentUser;
      if (!user) {
        customToast.error('Please login to view profile');
        navigate('/');
        return;
      }

      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists()) {
        const data = userDoc.data();
        
        // Validate plan status and update if needed
        const validatedData = await validateAndUpdatePlanStatus(data);
        
        setUserData(validatedData);
        setDisplayName(validatedData.displayName || user.displayName || '');
        setMobileNumber(validatedData.mobileNumber || '');
        setTempMobileNumber(validatedData.mobileNumber || '');
        setPhotoURL(validatedData.photoURL || user.photoURL || '');
        setPhotoPreview(validatedData.photoURL || user.photoURL || '');
      } else {
        // User document doesn't exist in Firestore, create basic user data
        const basicUserData = {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName || '',
          photoURL: user.photoURL || '',
          mobileNumber: '',
          skills: [],
          planType: 'free',
          isActivePlan: false,
          resumeDownloadsUsed: 0,
          coverLetterDownloadsUsed: 0,
          atsChecksUsed: 0,
          resumeDownloadLimit: 0,
          coverLetterDownloadLimit: 0,
          atsCheckLimit: 5,
          hasAICoaching: false,
          hasUnlimitedStorage: false,
          hasMockInterview: false,
          hasHRConsultancy: false,
          isEmailVerified: user.emailVerified,
          createdAt: new Date(),
          updatedAt: new Date()
        };
        setUserData(basicUserData);
        setDisplayName(user.displayName || '');
        setMobileNumber('');
        setTempMobileNumber('');
        setSkills([]);
        customToast.info('Profile loaded. Update your information!');
      }

      // Check if user has no email (in Firestore or Firebase Auth)
      // Show email collection modal for phone users who haven't set email yet
      const firestoreEmail = userDoc.exists() ? userDoc.data().email : '';
      if (!user.email && !firestoreEmail) {
        setShowEmailModal(true);
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      customToast.error('Failed to load profile: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        customToast.error('Please select an image file');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        customToast.error('Image size should be less than 5MB');
        return;
      }

      setPhotoFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadPhoto = async () => {
    if (!photoFile) return photoURL;

    try {
      setUploadingPhoto(true);
      const user = auth.currentUser;
      const storageRef = ref(storage, `profile-photos/${user.uid}/${Date.now()}_${photoFile.name}`);
      
      await uploadBytes(storageRef, photoFile);
      const downloadURL = await getDownloadURL(storageRef);
      
      setUploadingPhoto(false);
      return downloadURL;
    } catch (error) {
      console.error('Error uploading photo:', error);
      setUploadingPhoto(false);
      throw error;
    }
  };

  const handleSaveProfile = async () => {
    try {
      const user = auth.currentUser;
      if (!user) return;

      // Upload photo if changed
      let newPhotoURL = photoURL;
      if (photoFile) {
        newPhotoURL = await uploadPhoto();
      }

      // Update Firebase Auth profile (this updates displayName and photoURL in auth system)
      await updateProfile(user, {
        displayName: displayName,
        photoURL: newPhotoURL
      });

      const userDocRef = doc(db, 'users', user.uid);
      
      // Check if document exists
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists()) {
        // Update existing document
        await updateDoc(userDocRef, {
          displayName,
          photoURL: newPhotoURL,
          updatedAt: new Date()
        });
      } else {
        // Create new document with all required fields
        await setDoc(userDocRef, {
          uid: user.uid,
          email: user.email,
          displayName,
          photoURL: newPhotoURL,
          mobileNumber,
          planType: 'free',
          isActivePlan: false,
          resumeDownloadsUsed: 0,
          coverLetterDownloadsUsed: 0,
          atsChecksUsed: 0,
          resumeDownloadLimit: 0,
          coverLetterDownloadLimit: 0,
          atsCheckLimit: 5,
          hasAICoaching: false,
          hasUnlimitedStorage: false,
          hasMockInterview: false,
          hasHRConsultancy: false,
          isEmailVerified: user.emailVerified,
          createdAt: new Date(),
          updatedAt: new Date()
        });
      }

      customToast.success('Profile updated successfully!');
      setIsEditing(false);
      setPhotoFile(null);
      setPhotoURL(newPhotoURL);
      
      // Reload to refresh user data in navbar
      window.location.reload();
    } catch (error) {
      console.error('Error updating profile:', error);
      customToast.error('Failed to update profile: ' + error.message);
    }
  };

  const handleUpdateMobile = async () => {
    try {
      const user = auth.currentUser;
      if (!user) return;

      // Validate mobile number
      const mobileRegex = /^[+]?[0-9]{10,15}$/;
      if (!mobileRegex.test(tempMobileNumber)) {
        customToast.error('Please enter a valid mobile number');
        return;
      }

      const userDocRef = doc(db, 'users', user.uid);
      
      // Check if document exists
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists()) {
        await updateDoc(userDocRef, {
          mobileNumber: tempMobileNumber,
          updatedAt: new Date()
        });
      } else {
        // Create document if it doesn't exist
        await setDoc(userDocRef, {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName || '',
          photoURL: user.photoURL || '',
          mobileNumber: tempMobileNumber,
          skills: [],
          planType: 'free',
          isActivePlan: false,
          resumeDownloadsUsed: 0,
          coverLetterDownloadsUsed: 0,
          atsChecksUsed: 0,
          resumeDownloadLimit: 0,
          coverLetterDownloadLimit: 0,
          atsCheckLimit: 5,
          hasAICoaching: false,
          hasUnlimitedStorage: false,
          hasMockInterview: false,
          hasHRConsultancy: false,
          isEmailVerified: user.emailVerified,
          createdAt: new Date(),
          updatedAt: new Date()
        });
      }

      setMobileNumber(tempMobileNumber);
      customToast.success('Mobile number updated successfully!');
      fetchUserData();
    } catch (error) {
      console.error('Error updating mobile:', error);
      customToast.error('Failed to update mobile number: ' + error.message);
    }
  };

  const handleChangePassword = async () => {
    // Only email/password users can change password
    const user = auth.currentUser;
    const isEmailUser = user?.providerData?.some(p => p.providerId === 'password');
    if (!isEmailUser) {
      customToast.info('Password reset is only available for email/password login accounts.');
      return;
    }

    if (newPassword !== confirmPassword) {
      customToast.error('Passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      customToast.error('Password must be at least 6 characters');
      return;
    }

    try {
      const user = auth.currentUser;
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, newPassword);

      customToast.success('Password changed successfully!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      console.error('Error changing password:', error);
      if (error.code === 'auth/wrong-password') {
        customToast.error('Current password is incorrect');
      } else {
        customToast.error('Failed to change password');
      }
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      // Handle Firestore Timestamp
      if (timestamp.toDate && typeof timestamp.toDate === 'function') {
        return timestamp.toDate().toLocaleDateString('en-IN', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
      }
      // Handle JavaScript Date object
      if (timestamp instanceof Date) {
        return timestamp.toLocaleDateString('en-IN', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
      }
      // Handle timestamp string or number
      const date = new Date(timestamp);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString('en-IN', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
      }
      return 'N/A';
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'N/A';
    }
  };

  const getPlanBadgeClass = (planType, isActive) => {
    if (!isActive) {
      return 'plan-badge-inactive';
    }
    switch (planType?.toLowerCase()) {
      case 'silver':
        return 'plan-badge-silver';
      case 'gold':
        return 'plan-badge-gold';
      case 'platinum':
        return 'plan-badge-platinum';
      default:
        return 'plan-badge-free';
    }
  };

  const getPlanName = (planType, isActive) => {
    if (!isActive) {
      return 'Free';
    }
    return planType ? planType.charAt(0).toUpperCase() + planType.slice(1) : 'Free';
  };

  const isUnlimited = (value) => value === -1 || value === 'unlimited';

  // Validate plan status based on expiry date and download limits
  const validateAndUpdatePlanStatus = async (userData) => {
    try {
      const user = auth.currentUser;
      if (!user || !userData) return userData;

      let needsUpdate = false;
      const updates = {};

      // Check if plan has expired
      if (userData.planExpiryDate && userData.isActivePlan) {
        const expiryDate = userData.planExpiryDate.toDate ? userData.planExpiryDate.toDate() : new Date(userData.planExpiryDate);
        const currentDate = new Date();
        
        // Expiry day itself is still valid — only expire after end of expiry day
        const expiryEndOfDay = new Date(expiryDate.getFullYear(), expiryDate.getMonth(), expiryDate.getDate(), 23, 59, 59);
        
        if (currentDate > expiryEndOfDay) {
          updates.isActivePlan = false;
          updates.planType = 'free';
          needsUpdate = true;
          customToast.warning('Your plan has expired. Please upgrade to continue using premium features.');
        }
      }

      // Update Firestore if needed
      if (needsUpdate) {
        const userDocRef = doc(db, 'users', user.uid);
        await updateDoc(userDocRef, {
          ...updates,
          updatedAt: new Date()
        });
        
        // Return updated user data
        return {
          ...userData,
          ...updates
        };
      }

      return userData;
    } catch (error) {
      console.error('Error validating plan status:', error);
      return userData;
    }
  };

  if (loading) {
    return (
      <div className="profile-page">
        <div className="profile-loading">
          <div className="spinner"></div>
          <p>Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!userData) {
    return (
      <div className="profile-page">
        <div className="profile-loading">
          <p style={{ color: '#ef4444', fontSize: '1.2rem' }}>No user data available</p>
          <button 
            onClick={() => navigate('/')} 
            style={{
              marginTop: '20px',
              padding: '12px 24px',
              background: '#10b981',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer'
            }}
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-page">
      <div className="profile-container">
        
        {/* Header Section */}
        <div className="profile-header">
          <h1 className="profile-title">My Profile</h1>
        </div>

        <div className="profile-content">
          
          {/* Left Column - Personal Information */}
          <div className="profile-left">
            
            {/* User Info Card */}
            <div className="profile-card">
              <div className="card-header">
                <h2>Personal Information</h2>
                {!isEditing && (
                  <button className="edit-btn" onClick={() => setIsEditing(true)}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Edit
                  </button>
                )}
              </div>

              <div className="user-avatar-section">
                <div className="user-avatar-large">
                  {photoPreview || userData?.photoURL ? (
                    <img src={photoPreview || userData.photoURL} alt="Profile" />
                  ) : (
                    <div className="avatar-placeholder">
                      {userData?.displayName?.charAt(0)?.toUpperCase() || userData?.email?.charAt(0)?.toUpperCase() || 'U'}
                    </div>
                  )}
                </div>
                {isEditing && (
                  <div className="photo-upload-section">
                    <label htmlFor="photo-upload" className="photo-upload-btn">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <circle cx="12" cy="13" r="4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      {uploadingPhoto ? 'Uploading...' : 'Change Photo'}
                    </label>
                    <input
                      id="photo-upload"
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoChange}
                      style={{ display: 'none' }}
                      disabled={uploadingPhoto}
                    />
                    <p className="photo-upload-hint">Square image (1:1 ratio) recommended. Max 5MB.</p>
                  </div>
                )}
              </div>

              <div className="info-group">
                <label>Display Name</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="Enter your name"
                  />
                ) : (
                  <p>{displayName || 'Not set'}</p>
                )}
              </div>

              <div className="info-group">
                <label>Email Address</label>
                {!userData?.email ? (
                  <div className="input-with-button">
                    <p className="email-display" style={{ color: '#ef4444' }}>No email set</p>
                    <button className="update-btn" onClick={() => setShowEmailModal(true)}>
                      Add Email
                    </button>
                  </div>
                ) : (
                  <>
                    <p className="email-display">{userData.email}</p>
                    {userData?.isEmailVerified && (
                      <span className="verified-badge">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                        </svg>
                        Verified
                      </span>
                    )}
                  </>
                )}
              </div>

              <div className="info-group">
                <label>Mobile Number</label>
                <div className="input-with-button">
                  <input
                    type="tel"
                    value={tempMobileNumber}
                    onChange={(e) => setTempMobileNumber(e.target.value)}
                    placeholder="+91 XXXXX XXXXX"
                    maxLength="15"
                    disabled={!isEditing}
                  />
                  <button 
                    className={`update-btn ${!isEditing ? 'disabled' : ''}`} 
                    onClick={handleUpdateMobile}
                    disabled={!isEditing}
                  >
                    Update
                  </button>
                </div>
                <p className="current-value">Current: {mobileNumber || 'Not set'}</p>
              </div>

              {isEditing && (
                <div className="edit-actions">
                  <button className="cancel-btn" onClick={() => {
                    setIsEditing(false);
                    setDisplayName(userData?.displayName || '');
                    setPhotoPreview(userData?.photoURL || '');
                    setPhotoFile(null);
                  }}>
                    Cancel
                  </button>
                  <button className="save-btn" onClick={handleSaveProfile}>
                    Save Changes
                  </button>
                </div>
              )}
            </div>

            {/* Password Section */}
            {(() => {
              const isEmailPasswordUser = auth.currentUser?.providerData?.some(p => p.providerId === 'password') ?? false;
              const allFieldsFilled = currentPassword.trim() !== '' && newPassword.trim() !== '' && confirmPassword.trim() !== '';
              const resetBtnDisabled = !isEmailPasswordUser || !allFieldsFilled;
              const handleNonEmailClick = () => {
                if (!isEmailPasswordUser) {
                  customToast.info('Password reset is only available for Email/Password login accounts. Google, Phone OTP, and other provider accounts cannot change their password here.');
                }
              };
              return (
                <div className="profile-card">
                  <div className="card-header">
                    <h2>Reset Password</h2>
                    {!isEmailPasswordUser && (
                      <span className="password-unavailable-badge">Email login only</span>
                    )}
                  </div>

                  {/* Overlay captures clicks for non-email users and shows toast */}
                  <div
                    className={`password-change-section${!isEmailPasswordUser ? ' password-section-locked' : ''}`}
                    onClick={handleNonEmailClick}
                  >
                    <div className="info-group">
                      <label>Current Password</label>
                      <div className="password-input-wrapper">
                        <input
                          type={showCurrentPassword ? "text" : "password"}
                          value={currentPassword}
                          onChange={(e) => isEmailPasswordUser && setCurrentPassword(e.target.value)}
                          placeholder="Enter current password"
                          disabled={!isEmailPasswordUser}
                          className={!isEmailPasswordUser ? 'input-locked' : ''}
                        />
                        <button 
                          className="toggle-password-btn"
                          onClick={(e) => { e.stopPropagation(); isEmailPasswordUser && setShowCurrentPassword(!showCurrentPassword); }}
                          type="button"
                          disabled={!isEmailPasswordUser}
                        >
                          {showCurrentPassword ? (
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                              <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              <line x1="1" y1="1" x2="23" y2="23" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          ) : (
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              <circle cx="12" cy="12" r="3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          )}
                        </button>
                      </div>
                    </div>

                    <div className="info-group">
                      <label>New Password</label>
                      <div className="password-input-wrapper">
                        <input
                          type={showNewPassword ? "text" : "password"}
                          value={newPassword}
                          onChange={(e) => isEmailPasswordUser && setNewPassword(e.target.value)}
                          placeholder="Enter new password"
                          disabled={!isEmailPasswordUser}
                          className={!isEmailPasswordUser ? 'input-locked' : ''}
                        />
                        <button 
                          className="toggle-password-btn"
                          onClick={(e) => { e.stopPropagation(); isEmailPasswordUser && setShowNewPassword(!showNewPassword); }}
                          type="button"
                          disabled={!isEmailPasswordUser}
                        >
                          {showNewPassword ? (
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                              <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              <line x1="1" y1="1" x2="23" y2="23" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          ) : (
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              <circle cx="12" cy="12" r="3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          )}
                        </button>
                      </div>
                    </div>

                    <div className="info-group">
                      <label>Confirm New Password</label>
                      <div className="password-input-wrapper">
                        <input
                          type={showConfirmPassword ? "text" : "password"}
                          value={confirmPassword}
                          onChange={(e) => isEmailPasswordUser && setConfirmPassword(e.target.value)}
                          placeholder="Confirm new password"
                          disabled={!isEmailPasswordUser}
                          className={!isEmailPasswordUser ? 'input-locked' : ''}
                        />
                        <button 
                          className="toggle-password-btn"
                          onClick={(e) => { e.stopPropagation(); isEmailPasswordUser && setShowConfirmPassword(!showConfirmPassword); }}
                          type="button"
                          disabled={!isEmailPasswordUser}
                        >
                          {showConfirmPassword ? (
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                              <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              <line x1="1" y1="1" x2="23" y2="23" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          ) : (
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              <circle cx="12" cy="12" r="3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          )}
                        </button>
                      </div>
                    </div>

                    <button
                      className={`reset-password-btn${resetBtnDisabled ? ' btn-disabled' : ''}`}
                      onClick={(e) => { e.stopPropagation(); handleChangePassword(); }}
                      disabled={resetBtnDisabled}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M7 11V7a5 5 0 0 1 10 0v4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      Reset Password
                    </button>
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Right Column - Plan Details */}
          <div className="profile-right">
            
            {/* Current Plan Card */}
            <div className="profile-card plan-card">
              <div className="card-header">
                <h2>Current Plan</h2>
                <span className={`plan-badge ${getPlanBadgeClass(userData?.planType, userData?.isActivePlan)}`}>
                  {getPlanName(userData?.planType, userData?.isActivePlan)}
                </span>
              </div>

              <div className="plan-details">
                <div className="plan-detail-item">
                  <span className="detail-label">Plan Status</span>
                  <span className={`detail-value ${userData?.isActivePlan ? 'active' : 'inactive'}`}>
                    {userData?.isActivePlan ? '✓ Active' : '✗ Inactive'}
                  </span>
                </div>

                <div className="plan-detail-item">
                  <span className="detail-label">Plan Started</span>
                  <span className="detail-value">{formatDate(userData?.planStartDate)}</span>
                </div>

                <div className="plan-detail-item">
                  <span className="detail-label">Plan Expires</span>
                  <span className="detail-value">{formatDate(userData?.planExpiryDate)}</span>
                </div>
              </div>

              <button className="upgrade-plan-btn" onClick={() => navigate('/pricing')}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Upgrade/Renew Plan
              </button>
            </div>

            {/* Usage Statistics Card */}
            <div className="profile-card">
              <div className="card-header">
                <h2>Usage Statistics</h2>
                <button 
                  className={`refresh-stats-btn ${refreshingStats ? 'spinning' : ''}`}
                  onClick={async () => {
                    setRefreshingStats(true);
                    await fetchUserData();
                    setRefreshingStats(false);
                    customToast.success('Usage statistics updated');
                  }}
                  disabled={refreshingStats}
                  title="Refresh statistics"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>

              <div className="usage-stats">
                <div className="usage-item">
                  <div className="usage-header">
                    <span className="usage-label">Resume Downloads</span>
                    <span className="usage-value">
                      {userData?.resumeDownloadsUsed || 0} / {isUnlimited(userData?.resumeDownloadLimit) ? '∞' : userData?.resumeDownloadLimit || 0}
                    </span>
                  </div>
                  {!isUnlimited(userData?.resumeDownloadLimit) && (
                    <div className="usage-bar">
                      <div 
                        className="usage-bar-fill"
                        style={{ 
                          width: `${Math.min((userData?.resumeDownloadsUsed || 0) / (userData?.resumeDownloadLimit || 1) * 100, 100)}%` 
                        }}
                      ></div>
                    </div>
                  )}
                </div>

                <div className="usage-item">
                  <div className="usage-header">
                    <span className="usage-label">Cover Letter Downloads</span>
                    <span className="usage-value">
                      {userData?.coverLetterDownloadsUsed || 0} / {isUnlimited(userData?.coverLetterDownloadLimit) ? '∞' : userData?.coverLetterDownloadLimit || 0}
                    </span>
                  </div>
                  {!isUnlimited(userData?.coverLetterDownloadLimit) && (
                    <div className="usage-bar">
                      <div 
                        className="usage-bar-fill"
                        style={{ 
                          width: `${Math.min((userData?.coverLetterDownloadsUsed || 0) / (userData?.coverLetterDownloadLimit || 1) * 100, 100)}%` 
                        }}
                      ></div>
                    </div>
                  )}
                </div>

                <div className="usage-item">
                  <div className="usage-header">
                    <span className="usage-label">ATS Checks Performed</span>
                    <span className="usage-value">
                      {userData?.atsChecksUsed || 0} / {isUnlimited(userData?.atsCheckLimit) ? '∞' : userData?.atsCheckLimit || 5}
                    </span>
                  </div>
                  {!isUnlimited(userData?.atsCheckLimit) && (
                    <div className="usage-bar">
                      <div 
                        className="usage-bar-fill"
                        style={{ 
                          width: `${Math.min((userData?.atsChecksUsed || 0) / (userData?.atsCheckLimit || 5) * 100, 100)}%` 
                        }}
                      ></div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Email Collection Modal - shared component */}
      <EmailCollectionModal
        show={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        onEmailUpdated={(email) => {
          setShowEmailModal(false);
          // Refresh user data to reflect the new email
          fetchUserData();
        }}
      />
    </div>
  );
};

export default ProfilePage;
